import classNames from 'classnames';
import GoogleLogin, { type GoogleLoginResponse, type GoogleLoginResponseOffline } from 'react-google-login';
import { FormattedMessage } from 'react-intl';
import logSignUpScreenContinueButtonTapped from '@alltrails/amplitude/events/logSignUpScreenContinueButtonTapped';
import { post } from '@alltrails/core/utils/post';
import GoogleNoBackground from '@alltrails/denali/icons/GoogleNoBackground';
import altLoginStyles from '../AltLoginsForm/AltLoginsForm.module.scss';

type Props = {
  googleLoginHandler: (response: GoogleLoginResponse | GoogleLoginResponseOffline | string) => void;
  flatColors?: boolean;
};

// Standalone "Continue with Google" button. Extracted from InitialSimplified so it can be
// reused outside of the sign-up modal (e.g. the reviews auth wall) without pulling in the
// modal-only context that InitialSimplified depends on.
export default function GoogleLoginButton({ googleLoginHandler, flatColors = false }: Props) {
  // PLAT-6505
  const isUsingNewGoogleLoginButton = false;

  if (isUsingNewGoogleLoginButton) {
    return (
      <button
        className={classNames(altLoginStyles.google, { [altLoginStyles.flatColors]: flatColors })}
        onClick={() => {
          logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'google' });

          const client = window.google.accounts.oauth2.initCodeClient({
            client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
            scope: 'openid',
            callback: async ({ code }) => {
              const response = await post<{ token: string }>('/api/mugen/google/code', { code });
              googleLoginHandler(response.token);
            }
          });

          client.requestCode();
        }}
        type="button"
      >
        <GoogleNoBackground className={altLoginStyles.icon} />
        <span className={altLoginStyles.text}>
          <FormattedMessage defaultMessage="Continue with Google" />
        </span>
      </button>
    );
  }

  return (
    <GoogleLogin
      clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}
      cookiePolicy="single_host_origin"
      onSuccess={googleLoginHandler}
      render={renderProps => (
        <button
          className={classNames(altLoginStyles.google, { [altLoginStyles.flatColors]: flatColors })}
          onClick={() => {
            logSignUpScreenContinueButtonTapped({ signup_screen_continue_button_type: 'google' });
            renderProps.onClick();
          }}
          type="button"
        >
          <GoogleNoBackground className={altLoginStyles.icon} />
          <span className={altLoginStyles.text}>
            <FormattedMessage defaultMessage="Continue with Google" />
          </span>
        </button>
      )}
    />
  );
}
